import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { subDays } from 'date-fns';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  RUN_ANONYMIZATION_CHECK_DIALOG_CONFIRM_LABEL,
  RUN_ANONYMIZATION_CHECK_DIALOG_DESCRIPTION,
  RUN_ANONYMIZATION_CHECK_DIALOG_TITLE,
  RUN_ANONYMIZATION_CHECK_MAX_DATE_OFFSET_DAYS,
} from '../../../../constants/organizationsKeys';
import useWindowSize from '../../../../hooks/useWindowSize';
import { IRunCheckDateRange } from '../../../../types/organizations';
import { IDatePickerOutput } from '../../../../types/shell';
import { DialogCloseButton } from '../../../_mui';
import DateRangePicker from '../../inputs/DateRangePicker/DateRangePicker';

interface IProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (range: IRunCheckDateRange) => void;
}

const RunAnonymizationCheckDialog: React.FC<IProps> = ({ open, onClose, onConfirm }) => {
  const { mobile } = useWindowSize();

  const [dateRange, setDateRange] = useState<IDatePickerOutput | null>(null);
  const [maxDate, setMaxDate] = useState(() => subDays(new Date(), RUN_ANONYMIZATION_CHECK_MAX_DATE_OFFSET_DAYS));

  const isConfirmDisabled = useMemo(() => !dateRange?.from || !dateRange?.to, [dateRange]);

  useEffect(() => {
    if (open) {
      setMaxDate(subDays(new Date(), RUN_ANONYMIZATION_CHECK_MAX_DATE_OFFSET_DAYS));
    } else {
      setDateRange(null);
    }
  }, [open]);

  const handleDateRangeChange = useCallback((range?: IDatePickerOutput | null) => {
    setDateRange(range || null);
  }, []);

  const handleConfirm = useCallback(() => {
    const { from, to } = dateRange || {};

    if (from && to) {
      onConfirm({ from, to });
    }
  }, [dateRange, onConfirm]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      slotProps={{ paper: { sx: { overflow: 'visible' } } }}
      data-testid="run-anonymization-check-dialog"
    >
      <DialogTitle variant="h6" sx={{ pb: 2 }}>
        {RUN_ANONYMIZATION_CHECK_DIALOG_TITLE}
      </DialogTitle>
      <DialogCloseButton onClick={onClose} dataAction="close_run_anonymization_check_dialog" />
      <DialogContent sx={{ pt: 0, overflow: 'visible' }}>
        <DialogContentText color="text.secondary" variant="body1" sx={{ mb: 3 }}>
          {RUN_ANONYMIZATION_CHECK_DIALOG_DESCRIPTION}
        </DialogContentText>
        <Box display="flex" justifyContent="center">
          <DateRangePicker
            wide={!mobile}
            aboveDialog
            disablePortal
            maxDate={maxDate}
            value={dateRange}
            onSelect={handleDateRangeChange}
            onReset={handleDateRangeChange}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3, pt: 1 }}>
        <Button
          variant="text"
          size="small"
          onClick={onClose}
          data-action="cancel_run_anonymization_check_btn"
          sx={{ fontSize: 14 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          data-action="confirm_run_anonymization_check_btn"
          sx={{ fontSize: 14 }}
        >
          {RUN_ANONYMIZATION_CHECK_DIALOG_CONFIRM_LABEL}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RunAnonymizationCheckDialog;
